/**
 * Media Overlay Carousel controls.
 *
 * Standard carousels move the active slide's video controls into pagination and
 * drive slide progression outside Slick. Discovery carousels keep video
 * controls inside each slide and only reconcile active-slide playback.
 */
'use strict';

export function initMediaOverlayCarousels() {
    const CAROUSEL_SELECTOR = '[data-media-overlay-carousel]';
    const SLICK_SELECTOR = '.mediaOverlayCarousel__slick';
    const DOTS_SELECTOR = '.mediaOverlayCarousel__dots';
    const VIDEO_CONTROLS_SELECTOR = '.mediaOverlay__videoControls';
    const VIDEO_SELECTOR = '.mediaOverlay__video';
    const VIDEO_DOT_CLASS = 'mediaOverlayCarousel__dot--videoActive';
    const DISCOVERY_AUTOPLAY_ATTR = 'data-media-overlay-carousel-autoplay';
    const DISCOVERY_LOOP_ATTR = 'data-media-overlay-carousel-loop';
    const DISCOVERY_CLASS = 'mediaOverlayCarousel--discovery';
    const NAVIGATION_DARK_CLASS = 'mediaOverlayCarousel--navigation-dark';
    const NAVIGATION_LIGHT_CLASS = 'mediaOverlayCarousel--navigation-light';
    const SLIDE_NAVIGATION_LIGHT_CLASS =
        'mediaOverlayCarousel__slide--navigation-light';
    const DEFAULT_NAVIGATION_BUTTON_VARIANT = 'dark';
    const DEFAULT_AUTOPLAY_SPEED = 5000;

    /**
     * @typedef {Object} MediaOverlayCarouselVideoListener
     * @property {HTMLVideoElement} video - Video element with the listener.
     * @property {string} eventName - Native video event name.
     * @property {EventListener} handler - Registered event handler.
     */

    /**
     * @typedef {Object} MediaOverlayCarouselState
     * @property {number|null} timerId - Active autoplay timer ID.
     * @property {MediaOverlayCarouselVideoListener[]} videoListeners - Video listeners registered for the active slide.
     */

    /**
     * @typedef {Object} MediaOverlayVideoControlsApi
     * @property {function(): void} init - Initializes media overlay video controls.
     * @property {function(HTMLVideoElement, boolean): void} setPlayingState - Syncs the play/pause button state for carousel-owned playback.
     */

    /**
     * Restores video controls from the active pagination item to their original
     * Media Overlay placeholder before a real slide change.
     * @param {HTMLElement} root - Media Overlay Carousel root element.
     * @private
     */
    function _restoreVideoControls(root) {
        const activeVideoDot = root.querySelector(`.${VIDEO_DOT_CLASS}`);
        const controls = activeVideoDot
            ? activeVideoDot.querySelector(VIDEO_CONTROLS_SELECTOR)
            : null;

        if (!controls) return;

        const placeholder = controls._mediaOverlayCarouselPlaceholder;
        if (placeholder && placeholder.parentNode) {
            placeholder.parentNode.insertBefore(controls, placeholder);
            placeholder.parentNode.removeChild(placeholder);
        }

        controls._mediaOverlayCarouselPlaceholder = null;
        activeVideoDot.classList.remove(VIDEO_DOT_CLASS);
    }

    /**
     * Gets the current non-cloned slide when possible.
     * Slick can mark cloned slides as current near boundaries, so prefer the
     * matching real slide when possible.
     * @param {HTMLElement} root - Media Overlay Carousel root element.
     * @returns {HTMLElement|null} Active slide element.
     * @private
     */
    function _getActiveSlide(root) {
        const currentSlide = root.querySelector(
            `${SLICK_SELECTOR} .slick-current`,
        );

        if (!currentSlide) return null;
        if (!currentSlide.classList.contains('slick-cloned')) {
            return currentSlide;
        }

        const slickIndex = currentSlide.getAttribute('data-slick-index');

        if (!slickIndex) return currentSlide;

        return (
            root.querySelector(
                `${SLICK_SELECTOR} .slick-slide:not(.slick-cloned)[data-slick-index="${slickIndex}"]`,
            ) || currentSlide
        );
    }

    /**
     * Syncs the active slide's navigation button variant to the carousel root.
     * @param {HTMLElement} root - Media Overlay Carousel root element.
     * @private
     */
    function _syncNavigationButtonVariant(root) {
        const activeSlide = _getActiveSlide(root);
        const normalizedVariant =
            activeSlide &&
            activeSlide.classList.contains(SLIDE_NAVIGATION_LIGHT_CLASS)
                ? 'light'
                : DEFAULT_NAVIGATION_BUTTON_VARIANT;

        root.classList.remove(NAVIGATION_DARK_CLASS, NAVIGATION_LIGHT_CLASS);
        root.classList.add(
            normalizedVariant === 'light'
                ? NAVIGATION_LIGHT_CLASS
                : NAVIGATION_DARK_CLASS,
        );
    }

    /**
     * Gets the active Slick pagination item.
     * @param {HTMLElement} root - Media Overlay Carousel root element.
     * @returns {HTMLElement|null} Active dot list item.
     * @private
     */
    function _getActiveDot(root) {
        return root.querySelector(`${DOTS_SELECTOR} li.slick-active`);
    }

    /**
     * Checks whether an element and its ancestors are visible for the current
     * viewport. Mixed Media Overlay backgrounds keep both viewport layers in the
     * DOM and hide the inactive one with display utility classes.
     * @param {Element} element - Element to inspect.
     * @returns {boolean} True when the element is visible.
     * @private
     */
    function _isVisibleElement(element) {
        let current = element;

        while (current && current.nodeType === 1) {
            const style = window.getComputedStyle(current);

            if (style.display === 'none' || style.visibility === 'hidden') {
                return false;
            }

            current = current.parentElement;
        }

        return true;
    }

    /**
     * Finds the first element visible for the current viewport.
     * @param {HTMLElement} root - Root element to search within.
     * @param {string} selector - Selector to query.
     * @returns {Element|null} Visible element or null.
     * @private
     */
    function _queryVisibleElement(root, selector) {
        const elements = root.querySelectorAll(selector);

        for (let i = 0; i < elements.length; i++) {
            if (_isVisibleElement(elements[i])) {
                return elements[i];
            }
        }

        return null;
    }

    /**
     * Gets the autoplay speed configured for non-video slides.
     * @returns {number} Autoplay speed in milliseconds.
     * @private
     */
    function _getAutoplaySpeed() {
        return DEFAULT_AUTOPLAY_SPEED;
    }

    /**
     * Gets a stable state object for one carousel instance.
     * @param {HTMLElement} root - Media Overlay Carousel root element.
     * @returns {MediaOverlayCarouselState} Carousel autoplay state.
     * @private
     */
    function _getCarouselState(root) {
        if (!root._mediaOverlayCarouselState) {
            root._mediaOverlayCarouselState = {
                timerId: null,
                videoListeners: [],
            };
        }

        return root._mediaOverlayCarouselState;
    }

    /**
     * Clears pending autoplay timer.
     * @param {HTMLElement} root - Media Overlay Carousel root element.
     * @private
     */
    function _clearAutoplayTimer(root) {
        const state = _getCarouselState(root);

        if (state.timerId) {
            window.clearTimeout(state.timerId);
            state.timerId = null;
        }
    }

    /**
     * Tracks a video listener so it can be removed on slide changes.
     * @param {HTMLElement} root - Media Overlay Carousel root element.
     * @param {HTMLVideoElement} video - Video element.
     * @param {string} eventName - Native video event name.
     * @param {EventListener} handler - Event handler.
     * @private
     */
    function _addVideoListener(root, video, eventName, handler) {
        const state = _getCarouselState(root);

        video.addEventListener(eventName, handler);
        state.videoListeners.push({
            video: video,
            eventName: eventName,
            handler: handler,
        });
    }

    /**
     * Clears pending autoplay timer and active video listeners.
     * @param {HTMLElement} root - Media Overlay Carousel root element.
     * @private
     */
    function _clearAutoplay(root) {
        const state = _getCarouselState(root);

        _clearAutoplayTimer(root);

        for (let i = 0; i < state.videoListeners.length; i++) {
            const listener = state.videoListeners[i];

            listener.video.removeEventListener(
                listener.eventName,
                listener.handler,
            );
        }

        state.videoListeners = [];
    }

    /**
     * Gets the total number of real Slick slides.
     * @param {jQuery} $slick - Slick carousel instance.
     * @returns {number} Slide count.
     * @private
     */
    function _getSlideCount($slick) {
        const slickInstance = $slick.slick('getSlick');

        return slickInstance && slickInstance.slideCount
            ? slickInstance.slideCount
            : 0;
    }

    /**
     * Disables native autoplay/loop attributes from slide videos in this carousel.
     * @param {HTMLElement} root - Media Overlay Carousel root element.
     * @private
     */
    function _disableNativeVideoPlayback(root) {
        const videos = root.querySelectorAll(VIDEO_SELECTOR);

        for (let i = 0; i < videos.length; i++) {
            videos[i].removeAttribute('autoplay');
            videos[i].removeAttribute('loop');
            videos[i].autoplay = false;
            videos[i].loop = false;
        }
    }

    /**
     * Preserves authored Discovery playback intent while taking over native playback.
     * @param {HTMLElement} root - Media Overlay Carousel root element.
     * @private
     */
    function _disableDiscoveryNativeVideoPlayback(root) {
        const currentSlide = _getDiscoveryCurrentSlide(root);
        const canRemoveNativePlayback = document.readyState === 'complete';
        const videos = root.querySelectorAll(VIDEO_SELECTOR);

        for (let i = 0; i < videos.length; i++) {
            const hasNativeAutoplay =
                videos[i].autoplay || videos[i].hasAttribute('autoplay');
            const hasNativeLoop =
                videos[i].loop || videos[i].hasAttribute('loop');
            const shouldResetNativePlayback =
                hasNativeAutoplay &&
                !videos[i].hasAttribute(DISCOVERY_AUTOPLAY_ATTR);

            if (hasNativeAutoplay) {
                videos[i].setAttribute(DISCOVERY_AUTOPLAY_ATTR, 'true');
            }

            if (hasNativeLoop) {
                videos[i].setAttribute(DISCOVERY_LOOP_ATTR, 'true');
            }

            if (canRemoveNativePlayback) {
                videos[i].removeAttribute('autoplay');
                videos[i].removeAttribute('loop');
                videos[i].autoplay = false;
                videos[i].loop = false;
            }

            if (
                shouldResetNativePlayback &&
                (!currentSlide || !currentSlide.contains(videos[i]))
            ) {
                videos[i].pause();
                _resetVideo(videos[i]);
            }
        }
    }

    /**
     * Syncs the Media Overlay play/pause button with carousel-driven playback.
     * @param {HTMLVideoElement} video - Video element.
     * @param {boolean} isPlaying - Whether the video is playing.
     * @private
     */
    function _syncVideoToggleState(video, isPlaying) {
        if (
            window.mediaOverlayVideoControls &&
            typeof window.mediaOverlayVideoControls.setPlayingState ===
                'function'
        ) {
            window.mediaOverlayVideoControls.setPlayingState(video, isPlaying);
        }
    }

    /**
     * Pauses all videos in a slide without resetting currentTime.
     * @param {HTMLElement|null} slide - Slide element.
     * @private
     */
    function _pauseSlideVideos(slide) {
        if (!slide) return;

        const videos = slide.querySelectorAll(VIDEO_SELECTOR);

        for (let i = 0; i < videos.length; i++) {
            videos[i].pause();
        }
    }

    /**
     * Pauses carousel videos that are not in the active slide.
     * @param {HTMLElement} root - Media Overlay Carousel root element.
     * @param {HTMLElement|null} activeSlide - Active slide element.
     * @private
     */
    function _pauseInactiveVideos(root, activeSlide) {
        const videos = root.querySelectorAll(VIDEO_SELECTOR);

        for (let i = 0; i < videos.length; i++) {
            if (!activeSlide || !activeSlide.contains(videos[i])) {
                videos[i].pause();
            }
        }
    }

    /**
     * Gets the current visual Discovery slide.
     * @param {HTMLElement} root - Media Overlay Carousel root element.
     * @returns {HTMLElement|null} Current Slick slide.
     * @private
     */
    function _getDiscoveryCurrentSlide(root) {
        return root.querySelector(`${SLICK_SELECTOR} .slick-current`);
    }

    /**
     * Pauses every Discovery video except the provided one.
     * @param {HTMLElement} root - Media Overlay Carousel root element.
     * @param {HTMLVideoElement} activeVideo - Video that should keep playing.
     * @private
     */
    function _pauseDiscoveryOtherVideos(root, activeVideo) {
        const videos = root.querySelectorAll(VIDEO_SELECTOR);

        for (let i = 0; i < videos.length; i++) {
            if (videos[i] !== activeVideo) {
                videos[i].pause();
                _syncVideoToggleState(videos[i], false);
            }
        }
    }

    /**
     * Pauses videos outside the current active Discovery slide.
     * @param {HTMLElement} root - Media Overlay Carousel root element.
     * @private
     */
    function _pauseDiscoveryInactiveVideos(root) {
        const currentSlide = _getDiscoveryCurrentSlide(root);
        const videos = root.querySelectorAll(VIDEO_SELECTOR);

        for (let i = 0; i < videos.length; i++) {
            if (!currentSlide || !currentSlide.contains(videos[i])) {
                videos[i].pause();
                _syncVideoToggleState(videos[i], false);
            }
        }
    }

    /**
     * Reconciles the current Discovery slide video with its autoplay config.
     * Non-autoplay videos only have their toggle state synced.
     * @param {HTMLElement} root - Media Overlay Carousel root element.
     * @private
     */
    function _playDiscoveryActiveVideo(root) {
        const activeSlide = _getDiscoveryCurrentSlide(root);
        const activeVideo = activeSlide
            ? _queryVisibleElement(activeSlide, VIDEO_SELECTOR)
            : null;

        if (!activeVideo) return;

        if (!activeVideo.hasAttribute(DISCOVERY_AUTOPLAY_ATTR)) {
            _syncVideoToggleState(
                activeVideo,
                !activeVideo.paused && !activeVideo.ended,
            );
            return;
        }

        if (!activeVideo.paused && !activeVideo.ended) {
            _syncVideoToggleState(activeVideo, true);
            return;
        }

        if (activeVideo.ended) {
            _resetVideo(activeVideo);
        }

        try {
            const playPromise = activeVideo.play();

            if (playPromise && typeof playPromise.catch === 'function') {
                playPromise.catch(() => {
                    _syncVideoToggleState(activeVideo, false);
                });
            }

            _syncVideoToggleState(activeVideo, true);
        } catch (e) {
            // Browser autoplay policies can reject playback despite muted media.
            _syncVideoToggleState(activeVideo, false);
        }
    }

    /**
     * Reconciles Discovery video playback after carousel state changes.
     * @param {HTMLElement} root - Media Overlay Carousel root element.
     * @private
     */
    function _reconcileDiscoveryVideos(root) {
        _pauseDiscoveryInactiveVideos(root);
        _playDiscoveryActiveVideo(root);
    }

    /**
     * Ensures a user-started Discovery video pauses all other carousel videos.
     * @param {HTMLElement} root - Media Overlay Carousel root element.
     * @private
     */
    function _bindDiscoveryVideoPlaybackGuard(root) {
        const videos = root.querySelectorAll(VIDEO_SELECTOR);

        for (let i = 0; i < videos.length; i++) {
            if (videos[i]._mediaOverlayCarouselPlayBound) {
                continue;
            }

            videos[i]._mediaOverlayCarouselPlayBound = true;
            videos[i].addEventListener('play', () => {
                _pauseDiscoveryOtherVideos(root, videos[i]);
                _syncVideoToggleState(videos[i], true);
            });
            videos[i].addEventListener('pause', () => {
                _syncVideoToggleState(videos[i], false);
            });
            videos[i].addEventListener('ended', () => {
                const currentSlide = _getDiscoveryCurrentSlide(root);

                if (
                    videos[i].hasAttribute(DISCOVERY_LOOP_ATTR) &&
                    currentSlide &&
                    currentSlide.contains(videos[i])
                ) {
                    _resetVideo(videos[i]);

                    try {
                        const playPromise = videos[i].play();

                        if (
                            playPromise &&
                            typeof playPromise.catch === 'function'
                        ) {
                            playPromise.catch(() => {
                                _syncVideoToggleState(videos[i], false);
                            });
                        }

                        _syncVideoToggleState(videos[i], true);
                    } catch (e) {
                        _syncVideoToggleState(videos[i], false);
                    }

                    return;
                }

                _syncVideoToggleState(videos[i], false);
            });
        }
    }

    /**
     * Resets a completed video so revisiting its slide can play it again.
     * @param {HTMLVideoElement} video - Completed video.
     * @private
     */
    function _resetVideo(video) {
        try {
            video.currentTime = 0;
        } catch (e) {
            // Some browsers can reject seeking while video metadata is unstable.
        }
    }

    /**
     * Advances to the next slide when the carousel has more than one slide.
     * @param {jQuery} $slick - Slick carousel instance.
     * @private
     */
    function _goToNextSlide($slick) {
        if (_getSlideCount($slick) > 1) {
            $slick.slick('slickNext');
        }
    }

    /**
     * Schedules fallback autoplay when video autoplay cannot start.
     *
     * User-paused videos do not use this fallback; they hold the current slide
     * until playback resumes, the video ends, or the slide changes manually.
     *
     * @param {HTMLElement} root - Media Overlay Carousel root element.
     * @param {jQuery} $slick - Slick carousel instance.
     * @param {HTMLVideoElement} video - Active slide video.
     * @private
     */
    function _scheduleVideoFallbackAutoplay(root, $slick, video) {
        const currentSlide = _getActiveSlide(root);

        _clearAutoplayTimer(root);

        if (
            video.ended ||
            !currentSlide ||
            !currentSlide.contains(video) ||
            _getSlideCount($slick) <= 1
        ) {
            return;
        }

        const state = _getCarouselState(root);

        state.timerId = window.setTimeout(() => {
            const activeSlide = _getActiveSlide(root);

            state.timerId = null;

            if (activeSlide && activeSlide.contains(video)) {
                _goToNextSlide($slick);
            }
        }, _getAutoplaySpeed());
    }

    /**
     * Plays a video while falling back if autoplay is rejected.
     * @param {HTMLElement} root - Media Overlay Carousel root element.
     * @param {jQuery} $slick - Slick carousel instance.
     * @param {HTMLVideoElement} video - Active slide video.
     * @private
     */
    function _playVideo(root, $slick, video) {
        let playPromise;

        try {
            playPromise = video.play();
        } catch (e) {
            _scheduleVideoFallbackAutoplay(root, $slick, video);
            return;
        }

        if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => {
                _scheduleVideoFallbackAutoplay(root, $slick, video);
            });
        }
    }

    /**
     * Schedules carousel autoplay for the current slide.
     *
     * Image/color slides advance after a fixed delay. Video slides advance when
     * their native ended event fires. If a user pauses a video, the slide stays
     * active until playback resumes, the video ends, or the slide changes
     * manually.
     *
     * @param {HTMLElement} root - Media Overlay Carousel root element.
     * @param {jQuery} $slick - Slick carousel instance.
     * @private
     */
    function _scheduleActiveSlideAutoplay(root, $slick) {
        _clearAutoplay(root);

        const activeSlide = _getActiveSlide(root);

        _pauseInactiveVideos(root, activeSlide);

        if (!activeSlide) return;

        const activeVideo = _queryVisibleElement(activeSlide, VIDEO_SELECTOR);

        if (activeVideo) {
            const onVideoEnded = () => {
                _resetVideo(activeVideo);
                _clearAutoplay(root);
                _goToNextSlide($slick);
            };
            const onVideoPlay = () => _clearAutoplayTimer(root);

            _addVideoListener(root, activeVideo, 'ended', onVideoEnded);
            _addVideoListener(root, activeVideo, 'play', onVideoPlay);

            if (activeVideo.ended) {
                _resetVideo(activeVideo);
            }

            _playVideo(root, $slick, activeVideo);

            return;
        }

        if (_getSlideCount($slick) <= 1) return;

        const state = _getCarouselState(root);

        state.timerId = window.setTimeout(() => {
            state.timerId = null;
            _goToNextSlide($slick);
        }, _getAutoplaySpeed());
    }

    /**
     * Prevents video control interactions from bubbling into Slick pagination.
     * @param {Event} event - User interaction event.
     * @private
     */
    function _stopCarouselControlEvent(event) {
        event.stopPropagation();
    }

    /**
     * Binds event guards once on moved video controls.
     * @param {HTMLElement} controls - Media Overlay video controls element.
     * @private
     */
    function _bindVideoControlEvents(controls) {
        if (controls.hasAttribute('data-media-overlay-carousel-events-bound')) {
            return;
        }

        controls.setAttribute(
            'data-media-overlay-carousel-events-bound',
            'true',
        );
        controls.addEventListener('click', _stopCarouselControlEvent);
        controls.addEventListener('keydown', _stopCarouselControlEvent);
        controls.addEventListener('pointerdown', _stopCarouselControlEvent);
    }

    /**
     * Moves the active slide's video controls into the active pagination item.
     * For non-video slides, the active dot remains a normal Slick dot. If the
     * controls are already in the active dot (for example after a drag that did
     * not change slides), no DOM move is performed to avoid visual flicker.
     * @param {HTMLElement} root - Media Overlay Carousel root element.
     * @private
     */
    function _moveActiveVideoControls(root) {
        const activeDot = _getActiveDot(root);
        const activeDotControls = activeDot
            ? activeDot.querySelector(VIDEO_CONTROLS_SELECTOR)
            : null;

        if (
            activeDotControls &&
            activeDot.classList.contains(VIDEO_DOT_CLASS)
        ) {
            return;
        }

        _restoreVideoControls(root);

        const activeSlide = _getActiveSlide(root);
        const controls = activeSlide
            ? _queryVisibleElement(activeSlide, VIDEO_CONTROLS_SELECTOR)
            : null;

        if (!activeDot || !controls) return;

        const placeholder = document.createElement('span');

        placeholder.setAttribute('hidden', 'hidden');
        controls.parentNode.insertBefore(placeholder, controls);
        controls._mediaOverlayCarouselPlaceholder = placeholder;
        _bindVideoControlEvents(controls);
        activeDot.appendChild(controls);
        activeDot.classList.add(VIDEO_DOT_CLASS);
    }

    /**
     * Initializes one standard Media Overlay Carousel instance.
     * @param {HTMLElement} root - Media Overlay Carousel root element.
     * @param {HTMLElement} slick - Slick carousel element.
     * @private
     */
    function _initStandardCarousel(root, slick) {
        const $slick = window.jQuery(slick);

        root.setAttribute('data-media-overlay-carousel-initialized', 'true');
        _disableNativeVideoPlayback(root);
        _syncNavigationButtonVariant(root);
        _moveActiveVideoControls(root);
        _scheduleActiveSlideAutoplay(root, $slick);

        $slick.on('beforeChange.mediaOverlayCarousel', () => {
            const activeSlide = _getActiveSlide(root);

            _clearAutoplay(root);
            _pauseSlideVideos(activeSlide);
        });

        $slick.on('afterChange.mediaOverlayCarousel', () => {
            _disableNativeVideoPlayback(root);
            _syncNavigationButtonVariant(root);
            _moveActiveVideoControls(root);
            _scheduleActiveSlideAutoplay(root, $slick);
        });
    }

    /**
     * Initializes one Discovery Media Overlay Carousel instance.
     * @param {HTMLElement} root - Media Overlay Carousel root element.
     * @param {HTMLElement} slick - Slick root element.
     * @private
     */
    function _initDiscoveryCarousel(root, slick) {
        const $slick = window.jQuery(slick);

        root.setAttribute('data-media-overlay-carousel-initialized', 'true');
        _disableDiscoveryNativeVideoPlayback(root);
        _bindDiscoveryVideoPlaybackGuard(root);
        _reconcileDiscoveryVideos(root);

        if (document.readyState !== 'complete') {
            window.addEventListener(
                'load',
                () => {
                    _disableDiscoveryNativeVideoPlayback(root);
                    _reconcileDiscoveryVideos(root);
                },
                { once: true },
            );
        }

        $slick.on('afterChange.mediaOverlayCarousel', () => {
            _disableDiscoveryNativeVideoPlayback(root);
            _bindDiscoveryVideoPlaybackGuard(root);
            _reconcileDiscoveryVideos(root);

            if (window.mediaOverlayVideoControls) {
                window.mediaOverlayVideoControls.init();
            }
        });
    }

    /**
     * Initializes one Media Overlay Carousel instance.
     * @param {HTMLElement} root - Media Overlay Carousel root element.
     * @private
     */
    function _initCarousel(root) {
        if (root.hasAttribute('data-media-overlay-carousel-initialized')) {
            return;
        }

        const slick = root.querySelector(SLICK_SELECTOR);

        if (!slick) return;

        if (!window.jQuery || !window.jQuery.fn || !window.jQuery.fn.slick) {
            return;
        }

        if (root.classList.contains(DISCOVERY_CLASS)) {
            _initDiscoveryCarousel(root, slick);
            return;
        }

        _initStandardCarousel(root, slick);
    }

    const carousels = document.querySelectorAll(CAROUSEL_SELECTOR);

    for (let i = 0; i < carousels.length; i++) {
        _initCarousel(carousels[i]);
    }
}
